import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import { PRIVACY_POLICY, TERMS_OF_SERVICE } from '../../data/legalDocuments'
import type { LegalDocument } from '../../types/consent'
import LegalDocView from './LegalDocView'

interface Props {
  onAccept: () => Promise<void> | void
}

/**
 * Admin-app consent popup — single small card with collapsible accordions
 * for Terms of Service and Privacy Policy. One acknowledgement checkbox,
 * one accept button. No DPA flow on the admin side (super-admins aren't
 * tied to an organisation).
 */
export default function ConsentPopup({ onAccept }: Props) {
  const [agreed, setAgreed] = useState(false)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const titleId = useId()
  const cardRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    cardRef.current?.focus()
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  const handleAccept = async () => {
    if (!agreed || accepting) return
    setAccepting(true)
    setError(null)
    try {
      await onAccept()
    } catch (e) {
      console.error('Consent accept failed', e)
      setError('Could not record your acceptance. Please try again.')
      setAccepting(false)
    }
  }

  const docs: LegalDocument[] = [TERMS_OF_SERVICE, PRIVACY_POLICY]

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6"
      style={{ background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(2px)' }}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] outline-none"
      >
        <div className="px-6 pt-6 pb-4 flex items-start gap-3 border-b border-gray-100 dark:border-gray-700">
          <div className="w-10 h-10 rounded-2xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
            <ShieldCheckIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-white">
              Quick housekeeping
            </h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              Before you dive in, please confirm you've read our policies. Standard GDPR stuff — won't take a minute.
            </p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3 space-y-2">
          {docs.map((doc) => (
            <DocAccordion key={doc.type} doc={doc} />
          ))}
        </div>

        <div className="px-6 pt-4 pb-6 border-t border-gray-100 dark:border-gray-700 space-y-3">
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={accepting}
              className="mt-0.5 w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-xs text-gray-700 dark:text-gray-200 leading-relaxed">
              I've read and agree to the Terms of Service and Privacy Policy above. I confirm I am 18 or older.
            </span>
          </label>

          {error && (
            <p className="text-xs text-red-600 dark:text-red-400" role="alert">
              {error}
            </p>
          )}

          <button
            type="button"
            onClick={handleAccept}
            disabled={!agreed || accepting}
            className="w-full px-4 py-2.5 text-sm font-semibold rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            {accepting ? 'Saving…' : 'Accept and continue'}
          </button>
          <p className="text-[11px] text-center text-gray-400 dark:text-gray-500">
            You can review these any time from <strong className="font-semibold">Settings</strong>.
          </p>
        </div>
      </div>
    </div>,
    document.body
  )
}

function DocAccordion({ doc }: { doc: LegalDocument }) {
  const [open, setOpen] = useState(false)
  const headingId = useId()
  const panelId = useId()
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
      <button
        type="button"
        id={headingId}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <span className="text-sm font-semibold text-gray-900 dark:text-white">
          {labelFor(doc.type)}
        </span>
        <span className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
            {doc.version}
          </span>
          {open ? (
            <ChevronUpIcon className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDownIcon className="w-4 h-4 text-gray-400" />
          )}
        </span>
      </button>
      {open && (
        <div
          id={panelId}
          role="region"
          aria-labelledby={headingId}
          className="px-4 pb-4 pt-1 max-h-[40vh] overflow-y-auto bg-gray-50/50 dark:bg-gray-900/30"
        >
          <LegalDocView doc={doc} />
        </div>
      )}
    </div>
  )
}

function labelFor(type: LegalDocument['type']): string {
  switch (type) {
    case 'tos':
      return 'Terms of Service'
    case 'privacy':
      return 'Privacy Policy'
  }
}
